// Obsidian note formatting. Pure string builders — no I/O, no network.
// The browser copies the result to the clipboard; the CLI writer
// (scripts/add-note.mjs) produces the same shape for Dispatch/Claude to file.

import type { ParsedGenome, Variant } from '../types';
import type { ConditionMatch } from './conditions';
import type { GenomeStats } from './stats';

export interface NoteOptions {
  /** ISO date (YYYY-MM-DD). Passed in so output is testable/deterministic. */
  date: string;
  /**
   * When true, omit the raw genotype call — keep only rsID/position and
   * derived zygosity. Use this if notes might ever land in a synced vault.
   */
  redact?: boolean;
}

function zygosity(g: string): 'heterozygous' | 'homozygous' | 'no-call' | 'unknown' {
  if (g === '' || /[-.]/.test(g)) return 'no-call';
  if (g.length === 2) return g[0] === g[1] ? 'homozygous' : 'heterozygous';
  return 'unknown';
}

/** Obsidian-safe file stem for a variant note. */
export function variantNoteFilename(v: Variant): string {
  const base = v.rsid && v.rsid !== '.' ? v.rsid : `${v.chrom}-${v.pos}`;
  return base.replace(/[^\w.-]/g, '_');
}

/** A single-variant note with YAML frontmatter and [[wikilinks]]. */
export function variantNote(v: Variant, opts: NoteOptions): string {
  const zyg = zygosity(v.genotype);
  const fm: string[] = [
    '---',
    `rsid: ${v.rsid || '.'}`,
    `chromosome: "${v.chrom}"`,
    `position: ${v.pos}`,
    `zygosity: ${zyg}`,
  ];
  if (!opts.redact) fm.push(`genotype: ${v.genotype || '--'}`);
  if (v.ref) fm.push(`ref: ${v.ref}`);
  if (v.alt && !opts.redact) fm.push(`alt: ${v.alt}`);
  fm.push('source: i-seng', `created: ${opts.date}`, 'tags: [genome, variant]', '---');

  const title = v.rsid && v.rsid !== '.' ? v.rsid : `${v.chrom}:${v.pos}`;
  const genoLine = opts.redact
    ? `- **Genotype:** _redacted_ (${zyg})`
    : `- **Genotype:** \`${v.genotype || '--'}\` (${zyg})`;

  return [
    fm.join('\n'),
    '',
    `# ${title}`,
    '',
    `- **Location:** [[Chromosome ${v.chrom}]] · ${v.pos.toLocaleString()}`,
    genoLine,
    ...(v.ref ? [`- **Ref/Alt:** \`${v.ref}\` → \`${opts.redact ? '…' : v.alt ?? ''}\``] : []),
    '',
    '## Notes',
    '',
    '> _Why is this variant interesting? Add your observations here._',
    '',
    '---',
    'Related: [[Genome Index]]',
    '',
  ].join('\n');
}

/** A whole-genome summary note. */
export function summaryNote(
  genome: ParsedGenome,
  stats: GenomeStats,
  opts: NoteOptions,
): string {
  const pct = (x: number) => `${(x * 100).toFixed(1)}%`;
  const top = stats.perChrom.slice(0, 5).map((c) => `[[Chromosome ${c.chrom}]] (${c.count.toLocaleString()})`).join(', ');
  return [
    '---',
    'type: genome-summary',
    `format: ${genome.format}`,
    `source_file: "${genome.meta.fileName}"`,
    `variants: ${stats.total}`,
    `call_rate: ${pct(stats.callRate)}`,
    'source: i-seng',
    `created: ${opts.date}`,
    'tags: [genome, summary]',
    '---',
    '',
    `# Genome summary — ${genome.meta.fileName}`,
    '',
    `- **Variants:** ${stats.total.toLocaleString()} across ${stats.perChrom.length} chromosomes`,
    `- **Call rate:** ${pct(stats.callRate)} (${stats.noCalls.toLocaleString()} no-calls)`,
    `- **Heterozygosity:** ${pct(stats.hetRate)} (${stats.heterozygous.toLocaleString()} het / ${stats.homozygous.toLocaleString()} hom)`,
    `- **Ti/Tv:** ${stats.tiTv === null ? '—' : stats.tiTv.toFixed(2)}`,
    `- **Densest chromosomes:** ${top}`,
    '',
    '## Notes',
    '',
    '> _Session observations, questions to follow up, variants to revisit._',
    '',
    '---',
    'Related: [[Genome Index]]',
    '',
  ].join('\n');
}

/**
 * A note for one condition lookup: what the catalog claims, what your file
 * actually said, and the caveats — so a note filed in the vault carries its own
 * context instead of a bare "you have X".
 */
export function conditionNote(match: ConditionMatch, opts: NoteOptions): string {
  const c = match.condition;
  const lines: string[] = [
    '---',
    'type: condition-lookup',
    `condition: "${c.name}"`,
    `category: ${c.category}`,
    `impact: ${c.impact}`,
    `markers_called: ${match.covered}/${c.markers.length}`,
    'source: i-seng',
    `created: ${opts.date}`,
    'tags: [genome, condition, research-association]',
    '---',
    '',
    `# ${c.name}`,
    '',
    `> ${c.summary}`,
    '',
  ];

  if (c.caveat) lines.push(`**Caveat:** ${c.caveat}`, '');

  lines.push('## Markers', '');
  for (const m of match.markers) {
    const label = `[[${m.marker.rsid}]] (${m.marker.gene})`;
    if (m.status !== 'called') {
      lines.push(`- ${label} — _${STATUS_TEXT[m.status]}_`);
      continue;
    }
    const call = opts.redact ? '_redacted_' : `\`${m.genotype}\``;
    const copies = `${m.effectCopies} × \`${m.marker.effectAllele}\` (${m.marker.direction})`;
    const flags = [
      m.strandFlipped ? 'strand-flipped' : null,
      m.strandAmbiguous ? 'strand-ambiguous' : null,
    ].filter(Boolean);
    lines.push(
      `- ${label} — ${call}, ${copies}${flags.length ? ` · _${flags.join(', ')}_` : ''}`,
      `  - ${m.marker.effect}`,
    );
  }

  lines.push(
    '',
    '## Notes',
    '',
    '> _Questions to raise with a clinician? Anything to verify against the primary source?_',
    '',
    `**Sources:** ${c.sources}`,
    '',
    '> [!warning] Not a diagnosis',
    '> These are reported statistical associations from published research, not',
    '> a medical finding. Consumer array calls are not clinical-grade.',
    '',
    '---',
    'Related: [[Genome Index]]',
    '',
  );

  return lines.join('\n');
}

const STATUS_TEXT: Record<string, string> = {
  'no-call': 'present but not called',
  unreadable: 'call not readable as two bases',
  mismatch: "call doesn't match this SNP's alleles",
  absent: 'not in your file',
  called: 'called',
};

/** Best-effort clipboard copy that works over http/https and file://. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
